/**
 * useFillHeight — measure the height left between an element's top and the viewport bottom.
 *
 * The Research dashboard is a one-screen workbench: three panes that fill the viewport and
 * scroll internally, with no page scrollbar. Nothing in the layout hands down a height to fill
 * — `.mg-layout-content` is `min-height:100vh` on a plain <main>, and neither `.mg-page-container`
 * nor PageContainer's content div is a bounded flex column — so `flex: 1` resolves to nothing and
 * the height has to be measured.
 *
 * This mirrors DataTable's proven approach (see its fillHeight prop) rather than extending it:
 * DataTable's reserve (`53 + 39 + 56 + 2`) is table chrome — toolbar, header, pagination — and
 * every page using it depends on those exact numbers.
 *
 * `calc(100vh - Npx)` was the alternative and is wrong here: Sidebar.css swaps the content
 * padding to `padding-top: 108px` under 768px, so any constant is off by 48px there, and a miss
 * of even ~20px reintroduces the page scrollbar this screen exists to avoid.
 *
 * No measurement loop: we write `height` and read `top`, and `top` does not move when the
 * element's own height changes — the same reason DataTable's observer settles.
 *
 * Returns `undefined` when disabled, so callers can drop the fixed height and let the page
 * scroll normally (e.g. stacked on small screens).
 */

import { RefObject, useCallback, useEffect, useState } from 'react';

/** Bottom breathing room — matches .mg-layout-content's 12px bottom padding. */
const BOTTOM_GUTTER = 12;

/** Never collapse a pane to nothing, however cramped the viewport. */
const MIN_HEIGHT = 200;

export const useFillHeight = (
	ref: RefObject<HTMLElement>,
	enabled = true,
	offset = 0,
): number | undefined => {
	const [height, setHeight] = useState<number | undefined>(undefined);

	const measure = useCallback(() => {
		if (!enabled || !ref.current) return;
		const { top } = ref.current.getBoundingClientRect();
		const available = window.innerHeight - top - BOTTOM_GUTTER - offset;
		setHeight(Math.max(available, MIN_HEIGHT));
	}, [ref, enabled, offset]);

	useEffect(() => {
		if (!enabled) {
			setHeight(undefined);
			return;
		}

		measure();

		// The element's own box changing (a toolbar wrapping, a chip strip appearing) moves its
		// top, so observe it as well as the window.
		const observer = new ResizeObserver(measure);
		if (ref.current) observer.observe(ref.current);
		window.addEventListener('resize', measure);

		return () => {
			observer.disconnect();
			window.removeEventListener('resize', measure);
		};
	}, [enabled, measure, ref]);

	return height;
};

export default useFillHeight;
